import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { MaybeRef } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { remove, upsert } from '@/core/lib/array';
import { deepFreeze } from '@/core/lib/utils';
import { useOddsToken } from '@/core/oddsApi/composable/useOddsToken';
import { formatMarket } from '@/core/oddsApi/helpers/formatMarket';
import {
    useMarketsQuery,
    useMarketsSubscription,
    useMarketsV2Query,
    useMarketsV2Subscription,
} from '@/core/oddsApi/oddsApiType';
import type { IMarket } from '@/interface/IMarket';
import type { LeagueSize } from '@/interface/enum';
import { SiteStyle, SportType } from '@/interface/enum';
import { useToggleStore } from '@/store/toggleStore';

export function useMarkets(
    matchId: MaybeRef<number>,
    isLive: MaybeRef<boolean>,
    sportType: SportType,
    leagueSize: LeagueSize,
    leagueId: number,
    isEnabled: MaybeRef<boolean> = ref(true),
) {
    const markets = ref<IMarket[]>([]);
    const { isDynamicMarketMTokenEnabled } = storeToRefs(useToggleStore(SiteStyle.Asia));
    const { getOddsToken } = useOddsToken();

    const oldVariables = computed(() => ({
        query: {
            matchId: get(matchId),
            isLive: get(isLive),
        },
    }));

    const oldEnabled = computed(() => get(isEnabled) && !isDynamicMarketMTokenEnabled.value);

    const oldQuery = useMarketsQuery(
        oldVariables,
        deepCompareComputed(() => ({
            enabled: oldEnabled.value,
        })),
    );

    const oldSubscription = useMarketsSubscription(
        oldVariables,
        deepCompareComputed(() => ({
            enabled: oldEnabled.value,
        })),
    );

    const variables = computed(() => {
        const oddsToken = getOddsToken(sportType, leagueSize);

        return {
            query: {
                matchId: get(matchId),
                isLive: get(isLive),
                oddsToken,
                sportType: SportType.toOddsApi(sportType),
                leagueId,
            },
        };
    });

    const enabled = computed(() => get(isEnabled) && isDynamicMarketMTokenEnabled.value && !!variables.value.query.oddsToken);

    const query = useMarketsV2Query(
        variables,
        deepCompareComputed(() => ({
            enabled: enabled.value,
        })),
    );

    const subscription = useMarketsV2Subscription(
        variables,
        deepCompareComputed(() => ({
            enabled: enabled.value,
        })),
    );

    oldQuery.onResult((result) => {
        if (!result) return;
        markets.value = result.map(item => deepFreeze(formatMarket(item)));
    });

    query.onResult((result) => {
        if (!result) return;
        markets.value = result.map(item => deepFreeze(formatMarket(item)));
    });

    oldSubscription.onSubscription((payload) => {
        const toUpdate = (payload?.updated ?? [])
            .map(updated => deepFreeze(formatMarket(updated)));

        upsert(markets.value, toUpdate, item => item.id);
        remove(markets.value, payload?.deleted ?? [], (a, b) => a.id === b);
    });

    subscription.onSubscription((payload) => {
        const toUpdate = (payload?.updated ?? [])
            .map(updated => deepFreeze(formatMarket(updated)));

        upsert(markets.value, toUpdate, item => item.id);
        remove(markets.value, payload?.deleted ?? [], (a, b) => a.id === b);
    });

    const loaded = computed(() => (isDynamicMarketMTokenEnabled.value ? query.loaded.value : oldQuery.loaded.value));
    const loading = computed(() => (isDynamicMarketMTokenEnabled.value ? query.loading.value : oldQuery.loading.value));
    const refetch = () => (isDynamicMarketMTokenEnabled.value ? query.refetch() : oldQuery.refetch());

    return {
        markets,
        loaded,
        loading,
        refetch,
    };
}
